import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TranscriptWithPrompts } from "@/types/prompt";
import { supabase } from "../../../supabase/supabase";
import { useToast } from "@/components/ui/use-toast";
import { Eye, Trash2, Search, MessageSquare, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { NoData } from "@/components/ui/no-data";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Skeleton } from "../ui/skeleton";

export default function TranscriptsList() {
  const [transcripts, setTranscripts] = useState<TranscriptWithPrompts[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTranscripts();
  }, []);

  const fetchTranscripts = async () => {
    try {
      setLoading(true);
      const { data: transcriptsData, error: transcriptsError } = await supabase
        .from("transcripts")
        .select("*")
        .order("created_at", { ascending: false });

      if (transcriptsError) throw transcriptsError;

      // Fetch associated prompts for each transcript
      const transcriptsWithPrompts = await Promise.all(
        transcriptsData.map(async (transcript) => {
          const { data: promptsData, error: promptsError } = await supabase
            .from("prompts")
            .select("*")
            .eq("transcript_id", transcript.id);

          if (promptsError) throw promptsError;

          return {
            ...transcript,
            prompts: promptsData || [],
          };
        }),
      );

      setTranscripts(transcriptsWithPrompts);
    } catch (error) {
      console.error("Error fetching transcripts:", error);
      toast({
        title: "Error",
        description: "Failed to load transcripts. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewTranscript = (transcriptId: string) => {
    navigate(`/prompt-engine/transcript/${transcriptId}`);
  };

  const handleCreatePromptFromTranscript = (transcriptId: string) => {
    navigate(`/prompt-engine/create?transcriptId=${transcriptId}`);
  };

  const handleDeleteTranscript = async (transcriptId: string) => {
    try {
      // Check if there are any prompts using this transcript
      const transcript = transcripts.find((t) => t.id === transcriptId);
      if (transcript && transcript.prompts.length > 0) {
        toast({
          title: "Cannot Delete",
          description:
            "This transcript is being used by one or more prompts. Delete the prompts first.",
          variant: "destructive",
        });
        return;
      }

      // Delete the transcript
      const { error } = await supabase
        .from("transcripts")
        .delete()
        .eq("id", transcriptId);

      if (error) throw error;

      // Update the local state
      setTranscripts(transcripts.filter((t) => t.id !== transcriptId));

      toast({
        title: "Success",
        description: "Transcript deleted successfully.",
      });
    } catch (error) {
      console.error("Error deleting transcript:", error);
      toast({
        title: "Error",
        description: "Failed to delete transcript. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Function to get a preview of the transcript content
  const getTranscriptPreview = (content: string) => {
    return content.length > 150 ? content.substring(0, 150) + "..." : content;
  };

  const filteredTranscripts = transcripts.filter((transcript) =>
    transcript.content.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      {/* Only show search when loading or when there's data */}
      {(loading || transcripts.length > 0) && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search transcripts..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="overflow-hidden">
              <CardHeader className="pb-2">
                <Skeleton className="h-5 w-3/4 mb-2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-20 w-full" />
              </CardContent>
              <CardFooter className="flex justify-between">
                <Skeleton className="h-9 w-24" />
                <Skeleton className="h-9 w-24" />
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : transcripts.length === 0 ? (
        <NoData
          icon={<MessageSquare className="h-12 w-12 text-muted-foreground/50" />}
          title="No transcripts found"
          description="You haven't created any transcripts yet. Create your first transcript to get started."
          actionButton={
            <Button
              variant="primaryOutline"
              onClick={() => navigate("/prompt-engine/transcript/create")}
              className="mt-2"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Your First Transcript
            </Button>
          }
        />
      ) : filteredTranscripts.length === 0 ? (
        <NoData
          icon={<MessageSquare className="h-12 w-12 text-muted-foreground/50" />}
          title="No transcripts match your search"
          description="No transcripts match your search criteria. Try a different search term."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredTranscripts.map((transcript) => (
            <Card key={transcript.id} className="overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">
                  {transcript.name ||
                    `Transcript ${transcript.id.substring(0, 8)}`}
                </CardTitle>
                <CardDescription>
                  {new Date(transcript.created_at).toLocaleDateString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground line-clamp-3">
                  {getTranscriptPreview(transcript.content)}
                </p>
                <div className="mt-2 flex items-center text-xs text-muted-foreground">
                  <MessageSquare className="h-3 w-3 mr-1" />
                  <span>
                    {transcript.prompts.length} prompt
                    {transcript.prompts.length !== 1 ? "s" : ""} created
                  </span>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewTranscript(transcript.id)}
                  >
                    <Eye className="h-4 w-4 mr-1" /> View
                  </Button>
                  <Button
                    variant="primaryOutline"
                    size="sm"
                    onClick={() =>
                      handleCreatePromptFromTranscript(transcript.id)
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" /> Create Prompt
                  </Button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => handleDeleteTranscript(transcript.id)}
                  disabled={transcript.prompts.length > 0}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
